// src/services/api.ts - Production-ready API configuration
import axios from 'axios';

// ==================== ENVIRONMENT DETECTION ====================

/**
 * Determine if we're in production mode
 * Checks multiple indicators to be certain
 */
const isProduction = (): boolean => {
  return (
    import.meta.env.PROD || // Vite production build
    import.meta.env.MODE === 'production' || // Explicit production mode
    import.meta.env.VITE_ENV === 'production' || // Custom env variable
    window.location.hostname !== 'localhost' // Not on localhost
  );
};

/**
 * Get API Base URL based on environment
 * - Production: Uses Railway backend
 * - Development: Uses localhost
 */
const getApiBaseUrl = (): string => {
  // Check if custom URL is provided via env var
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  
  // Fallback based on environment
  if (isProduction()) {
    return 'https://janet-marketing-backend-production.up.railway.app/api';
  }
  
  return 'https://janet-marketing-backend-production.up.railway.app/api';
};

const API_BASE_URL = getApiBaseUrl();

// ==================== CONFIGURATION LOGGING ====================

console.log('🌍 API Configuration:', {
  baseURL: API_BASE_URL,
  environment: import.meta.env.VITE_ENV || 'development',
  mode: import.meta.env.MODE,
  isProduction: isProduction(),
  hostname: window.location.hostname,
});

// ==================== AXIOS INSTANCE ====================

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds for production (Railway can be slower)
});

// ==================== REQUEST INTERCEPTOR ====================

api.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Log outgoing requests in development only
    if (!isProduction()) {
      console.log(`📤 ${config.method?.toUpperCase()} ${config.url}`);
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ==================== RESPONSE INTERCEPTOR ====================

api.interceptors.response.use(
  (response) => {
    // Log responses in development only
    if (!isProduction()) {
      console.log(`📥 Response from ${response.config.url}:`, response.data);
    }
    
    // Extract data from ApiResponse wrapper
    if (response.data && response.data.success !== undefined) {
      // Backend returns: { success: true, message: "...", data: {...} }
      return {
        ...response,
        data: response.data.data,
        message: response.data.message,
      };
    }
    return response;
  },
  (error) => {
    // Handle 401 - Token expired/invalid
    if (error.response?.status === 401) {
      console.warn('🔒 Unauthorized - clearing session');
      sessionStorage.removeItem('adminToken');
      sessionStorage.removeItem('adminUsername');
      sessionStorage.removeItem('adminRole');
      
      // Redirect to login
      if (window.location.pathname.startsWith('/admin')) {
        window.location.href = '/admin';
      }
    }

    // Handle 403 - Forbidden
    if (error.response?.status === 403) {
      console.error('⛔ Access forbidden:', error.response.data);
    }

    // Extract error message from ApiResponse
    const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
    
    // Only log in development
    if (!isProduction()) {
      console.error('❌ API Error:', {
        url: error.config?.url,
        status: error.response?.status,
        message: errorMessage
      });
    }
    
    return Promise.reject({
      message: errorMessage,
      status: error.response?.status,
      data: error.response?.data,
    });
  }
);

// ==================== AUTH ENDPOINTS ====================

export const authAPI = {
  login: async (credentials: { username: string; password: string }) => {
    const response = await api.post('/admin/login', credentials);
    return response.data;
  },

  getSessionInfo: async () => {
    const response = await api.get('/admin/session-info');
    return response.data;
  },

  extendSession: async () => {
    const response = await api.post('/admin/extend-session');
    return response.data;
  },

  updatePassword: async (passwordData: { currentPassword: string; newPassword: string }) => {
    const response = await api.put('/admin/password', passwordData);
    return response.data;
  },

  revokeAdmin: async (username: string) => {
    const response = await api.post(`/admin/revoke/${username}`);
    return response.data;
  },
};

// ==================== DASHBOARD ENDPOINTS ====================

export const dashboardAPI = {
  getFullDashboard: async () => {
    const response = await api.get('/admin/dashboard');
    return response.data;
  },

  poll: async () => {
    const response = await api.get('/admin/dashboard/poll');
    return response.data;
  },

  getRecentUpdates: async (minutes = 2) => {
    const response = await api.get(`/admin/dashboard/recent?minutes=${minutes}`);
    return response.data;
  },
};

// ==================== LOGIN LOGS ENDPOINTS ====================

export const loginLogsAPI = {
  getAll: async (page = 0, size = 20) => {
    const response = await api.get(`/admin/login-logs?page=${page}&size=${size}`);
    return response.data;
  },

  getByUsername: async (username: string, page = 0, size = 20) => {
    const response = await api.get(`/admin/login-logs/${username}?page=${page}&size=${size}`);
    return response.data;
  },

  getRecent: async (minutes = 5) => {
    const response = await api.get(`/admin/login-logs/recent?minutes=${minutes}`);
    return response.data;
  },
};

// ==================== METRICS ENDPOINTS ====================

export const metricsAPI = {
  getMetrics: async (period = 'today', startDate: string | null = null, endDate: string | null = null) => {
    let url = `/admin/metrics?period=${period}`;
    if (period === 'custom' && startDate && endDate) {
      url += `&startDate=${startDate}&endDate=${endDate}`;
    }
    const response = await api.get(url);
    return response.data;
  },
};

// ==================== ENROLLMENT ENDPOINTS ====================

export const enrollmentAPI = {
  /**
   * Create enrollment
   * POST /api/enrollments
   */
  create: async (enrollmentData: any) => {
    const response = await api.post('/enrollments', enrollmentData);
    return response.data;
  },

  /**
   * Get enrollment by ID
   * GET /api/enrollments/{id}
   */
  getById: async (id: number) => {
    const response = await api.get(`/enrollments/${id}`);
    return response.data;
  },

  /**
   * Delete enrollment by ID (Admin only - requires JWT)
   * DELETE /api/enrollments/{id}
   */
  delete: async (id: number): Promise<void> => {
    try {
      await api.delete(`/enrollments/${id}`);
      return;
    } catch (error: any) {
      if (error.status === 404) {
        throw new Error('Enrollment not found. It may have already been deleted.');
      } 
      if (error.status === 403) {
        throw new Error('You do not have permission to delete enrollments.');
      } 
      if (error.status === 401) {
        throw new Error('Session expired. Please log in again.');
      }
      if (error.status === 500) {
        throw new Error('Server error occurred while deleting. Please try again.');
      }
      throw new Error(error.message || 'Failed to delete enrollment. Please try again.');
    }
  },

  /**
   * Mark enrollment as added to WhatsApp group (Admin only - requires JWT)
   * PATCH /api/enrollments/{id}/whatsapp-status
   */
  markAsAddedToWhatsApp: async (enrollmentId: number): Promise<void> => {
    try {
      await api.patch(`/enrollments/${enrollmentId}/whatsapp-status`, {
        addedToWhatsApp: true
      });
      return;
    } catch (error: any) {
      if (error.status === 404) {
        throw new Error('Enrollment not found.');
      }
      if (error.status === 403) {
        throw new Error('You do not have permission to update enrollments.');
      }
      if (error.status === 401) {
        throw new Error('Session expired. Please log in again.');
      }
      throw new Error(error.message || 'Failed to update WhatsApp status');
    }
  },
};

// ==================== PAYMENT ENDPOINTS ====================

export const paymentAPI = {
  /**
   * Initiate payment
   * POST /api/payments
   */
  initiate: async (enrollmentId: number, phoneNumber: string) => {
    const response = await api.post('/payments', {
      enrollmentId,
      phoneNumber
    });
    return response.data;
  },

  /**
   * Get payment status (for polling)
   * GET /api/payments/status/{transactionRef}
   */
  checkStatus: async (transactionRef: string) => {
    const response = await api.get(`/payments/status/${transactionRef}`);
    return response.data;
  },

  /**
   * Retry failed payment (Admin only - requires JWT)
   * POST /api/payments/retry
   */
  retry: async (enrollmentId: number) => {
    try {
      const response = await api.post('/payments/retry', { enrollmentId });
      return response.data;
    } catch (error: any) {
      if (error.status === 404) {
        throw new Error('Enrollment not found.');
      }
      if (error.status === 400) {
        throw new Error(error.message || 'Invalid payment retry request.');
      }
      throw new Error(error.message || 'Failed to retry payment. Please try again.');
    }
  },
};

// ==================== ADMIN MANAGEMENT ENDPOINTS ====================

export const adminManagementAPI = {
  /**
   * List all admins (SUPER_ADMIN only)
   * GET /api/admin/list
   */
  listAdmins: async () => {
    const response = await api.get('/admin/list');
    return response.data;
  },

  /**
   * Create new admin (SUPER_ADMIN only)
   * POST /api/admin/create
   */
  createAdmin: async (data: { username: string; password: string; role: 'SUPER_ADMIN' | 'ADMIN' }) => {
    const response = await api.post('/admin/create', data);
    return response.data;
  },

  /**
   * Revoke admin access (SUPER_ADMIN only)
   * POST /api/admin/revoke/{username}
   */
  revokeAdmin: async (username: string) => {
    const response = await api.post(`/admin/revoke/${username}`);
    return response.data;
  },
};

export default api;